import { type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setToken, setUser } from '../lib/auth.ts';
import { API_BASE } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import { type AuthResponse, emailLocale } from '../lib/verification.ts';
import { ForgotPassword } from './ForgotPassword.tsx';
import { Spinner } from './authUi.tsx';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const isGoogleEnabled =
  !!GOOGLE_CLIENT_ID &&
  GOOGLE_CLIENT_ID !== 'placeholder_configure_later' &&
  GOOGLE_CLIENT_ID !== 'placeholder';

type AuthMode = 'login' | 'register';

interface AuthGateProps {
  onAuth: () => void;
}

export function AuthGate({ onAuth }: Readonly<AuthGateProps>) {
  const { t } = useTranslation();
  const [forgot, setForgot] = useState(false);
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const storeSetUser = useStore((s) => s.setUser);

  const handleSuccess = useCallback(
    (data: AuthResponse) => {
      setToken(data.token);
      setUser(data.user);
      storeSetUser(data.user);
      onAuth();
    },
    [onAuth, storeSetUser],
  );

  const handleGoogleCallback = useCallback(
    async (response: { credential: string }) => {
      setLoading(true);
      setError('');
      try {
        const resp = await fetch(`${API_BASE}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: response.credential }),
        });
        const data = await resp.json();
        if (resp.ok) {
          handleSuccess(data as AuthResponse);
        } else {
          setError(data.error || `Error ${resp.status}`);
        }
      } catch {
        setError('Cannot reach server');
      } finally {
        setLoading(false);
      }
    },
    [handleSuccess],
  );

  useEffect(() => {
    if (!isGoogleEnabled) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      const goog = (globalThis as typeof globalThis & { google?: { accounts: { id: { initialize: (opts: object) => void; renderButton: (el: HTMLElement | null, opts: object) => void } } } }).google;
      goog?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });
      goog?.accounts.id.renderButton(
        document.getElementById('google-signin-btn'),
        { theme: 'outline', size: 'large', width: '100%', text: 'continue_with' },
      );
    };
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [handleGoogleCallback]);

  const handleLogin = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      const trimEmail = email.trim();
      const trimPass = password;
      if (!trimEmail || !trimPass) return;

      setLoading(true);
      setError('');

      try {
        const resp = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimEmail, password: trimPass }),
        });

        const data = await resp.json();
        if (resp.ok) {
          handleSuccess(data as AuthResponse);
        } else {
          setError(data.error || `Error ${resp.status}`);
        }
      } catch {
        setError('Cannot reach server');
      } finally {
        setLoading(false);
      }
    },
    [email, password, handleSuccess],
  );

  const handleRegister = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      const trimEmail = email.trim();
      const trimName = name.trim();
      const trimPass = password;

      if (!trimEmail || !trimName || !trimPass) return;
      if (trimPass !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (trimPass.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const resp = await fetch(`${API_BASE}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: trimEmail,
            password: trimPass,
            name: trimName,
            locale: emailLocale(),
          }),
        });

        const data = await resp.json();
        if (resp.ok) {
          handleSuccess(data as AuthResponse);
        } else {
          const errMsg = Array.isArray(data.error) ? data.error.join(', ') : data.error;
          setError(errMsg || `Error ${resp.status}`);
        }
      } catch {
        setError('Cannot reach server');
      } finally {
        setLoading(false);
      }
    },
    [email, password, confirmPassword, name, handleSuccess],
  );

  if (forgot) {
    return (
      <ForgotPassword
        initialEmail={email.trim()}
        onBack={() => { setForgot(false); setError(''); }}
        onSuccess={handleSuccess}
      />
    );
  }

  let submitLabel: string;
  if (loading) {
    submitLabel = 'Connecting...';
  } else if (mode === 'login') {
    submitLabel = 'Sign In';
  } else {
    submitLabel = 'Create Account';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-(--color-bg-primary)">
      <form
        onSubmit={mode === 'login' ? handleLogin : handleRegister}
        className="w-full max-w-sm mx-4 rounded-2xl bg-(--color-bg-secondary) border border-(--color-border) p-8 flex flex-col gap-5 shadow-xl"
      >
        {/* Header */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-4xl">{'\u{1F6B4}'}</span>
          <h1 className="text-xl font-bold text-(--color-text-primary)">Cycling Coach</h1>
          <p className="text-sm text-(--color-text-secondary)">
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex rounded-lg bg-(--color-bg-tertiary) p-1">
          <button
            type="button"
            onClick={() => { setMode('login'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'login'
                ? 'bg-(--color-bg-secondary) text-(--color-text-primary) shadow-sm'
                : 'text-(--color-text-secondary)'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => { setMode('register'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'register'
                ? 'bg-(--color-bg-secondary) text-(--color-text-primary) shadow-sm'
                : 'text-(--color-text-secondary)'
            }`}
          >
            Register
          </button>
        </div>

        {/* Name (register only) */}
        {mode === 'register' && (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            autoComplete="name"
            className="w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2.5 text-(--color-text-primary) placeholder-(--color-text-secondary) text-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent)/50"
          />
        )}

        {/* Email */}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          autoComplete="email"
          autoFocus
          className="w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2.5 text-(--color-text-primary) placeholder-(--color-text-secondary) text-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent)/50"
        />

        {/* Password */}
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            className="w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2.5 pr-12 text-(--color-text-primary) placeholder-(--color-text-secondary) text-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent)/50"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-primary) text-xs px-1.5 py-1 rounded"
          >
            {show ? 'Hide' : 'Show'}
          </button>
        </div>

        {mode === 'login' && (
          <button
            type="button"
            onClick={() => { setForgot(true); setError(''); }}
            className="-mt-3 self-end text-xs text-(--color-accent) hover:underline"
          >
            {t('auth.forgotLink')}
          </button>
        )}

        {/* Confirm password (register only) */}
        {mode === 'register' && (
          <input
            type={show ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            autoComplete="new-password"
            className="w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2.5 text-(--color-text-primary) placeholder-(--color-text-secondary) text-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent)/50"
          />
        )}

        {/* Error */}
        {error && (
          <p className="text-red-400 text-sm text-center">{error}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg font-semibold text-sm bg-green-400 text-gray-900 hover:bg-green-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {loading && <Spinner />}
          {submitLabel}
        </button>

        {/* Google OAuth */}
        {isGoogleEnabled && (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-(--color-border)" />
              <span className="text-xs text-(--color-text-secondary)">or</span>
              <div className="flex-1 h-px bg-(--color-border)" />
            </div>
            <div id="google-signin-btn" className="flex justify-center" />
          </>
        )}

        {/* Toggle link */}
        <p className="text-xs text-center text-(--color-text-secondary)">
          {mode === 'login' ? (
            <>
              {"Don't have an account? "}
              <button
                type="button"
                onClick={() => { setMode('register'); setError(''); }}
                className="text-(--color-accent) hover:underline"
              >
                Register
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                className="text-(--color-accent) hover:underline"
              >
                Sign in
              </button>
            </>
          )}
        </p>

        <p className="text-[11px] text-center text-(--color-text-secondary)">
          By continuing you agree to the{' '}
          <a href="/terms" className="underline hover:text-(--color-text-primary)">Terms</a>
          {' and '}
          <a href="/privacy" className="underline hover:text-(--color-text-primary)">Privacy Policy</a>.
        </p>
      </form>
    </div>
  );
}
